import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Container, Row, Col, Form, Button } from 'react-bootstrap'
import {
  addDoc,
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from 'firebase/firestore'
import { db } from '../firebase'
import Footer from './footer'
import './RoomDetail.css'

// small helpers moved out so they're stable across renders

// Quiet debug helpers; set to true for local debugging
const DEBUG = false
const debugLog = (...args) => {
  if (DEBUG) console.log(...args)
}
const debugWarn = (...args) => {
  if (DEBUG) console.warn(...args)
}
const debugError = (...args) => {
  if (DEBUG) console.error(...args)
}

const getUrl = (item) => {
  if (!item) return null
  if (typeof item === 'string') return item.trim() || null
  if (typeof item === 'object') {
    const candidates = [
      item.url,
      item.src,
      item.path,
      item.downloadURL,
      item.image,
      item.fileUrl,
      item.fullPath,
    ]
    for (const c of candidates)
      if (c && typeof c === 'string' && c.trim()) return c.trim()
  }
  return null
}

// timezone-safe helpers: parse YYYY-MM-DD into a local Date (no timezone shift)
const parseISOToLocalDate = (iso) => {
  if (!iso) return null
  if (typeof iso === 'string') {
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  }
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

const formatLocalISO = (d) => {
  if (!d) return ''
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

// Format a Date or ISO string as DD/MM/YYYY for UI display
const formatDDMMYYYY = (input) => {
  if (!input) return ''
  let d = null
  if (typeof input === 'string') {
    // try ISO YYYY-MM-DD first
    const parsed = parseISOToLocalDate(input)
    if (parsed) d = parsed
    else {
      const dd = new Date(input)
      if (!Number.isNaN(dd.getTime())) d = dd
    }
  } else if (input instanceof Date) {
    d = input
  }
  if (!d) return ''
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  return `${day}/${month}/${year}`
}

// Device detection: true for phones/tablets, false for desktops/laptops
const isMobileOrTablet = () => {
  try {
    if (typeof navigator === 'undefined') return false
    const ua = navigator.userAgent || navigator.vendor || ''
    const isIPadOS =
      // iPadOS 13+ reports as Mac, detect via touch
      /Macintosh/.test(ua) && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1
    const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i
    return isIPadOS || mobileRegex.test(ua)
  } catch {
    return false
  }
}

// ImageViewer: shows up to 4 valid images from the provided images array and a lightbox
const ImageViewer = React.memo(({ images }) => {
  // normalize and keep only truthy URLs
  const imgs = useMemo(
    () => (Array.isArray(images) ? images.map(getUrl).filter(Boolean) : []),
    [images]
  )

  // display at most 4 images in the grid
  const MAX_DISPLAY = 4
  const displayImgs = imgs.slice(0, MAX_DISPLAY)
  const displayCount = displayImgs.length
  const totalCount = imgs.length

  // create an array of cell indexes equal to the number of images to display
  // if displayCount is 0 this becomes an empty array (no placeholder cell)
  const cells = Array.from({ length: displayCount }, (_, i) => i)

  const [open, setOpen] = useState(false)
  const [index, setIndex] = useState(0)
  const [hovered, setHovered] = useState(null)

  const openAt = (i) => {
    setIndex(displayCount ? i % displayCount : 0)
    setOpen(true)
  }
  const close = () => setOpen(false)
  const next = useCallback(
    () => setIndex((s) => (displayCount ? (s + 1) % displayCount : 0)),
    [displayCount]
  )
  const prev = useCallback(
    () =>
      setIndex((s) =>
        displayCount ? (s - 1 + displayCount) % displayCount : 0
      ),
    [displayCount]
  )

  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === 'Escape') close()
      if (e.key === 'ArrowRight') next()
      if (e.key === 'ArrowLeft') prev()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, next, prev])

  // Render only the valid images (up to MAX_DISPLAY). If none, show placeholder cell.
  return (
    <div
      className={`image-viewer-card ${hovered !== null ? 'hovering' : ''} ${hovered !== null ? `hover-${hovered}` : ''
        }`}
    >
      <div className={`iv-grid iv-grid-count-${displayCount || 0}`}>
        {cells.map((i) => {
          const src = displayImgs[i]
          return (
            <button
              key={i}
              className={`iv-item iv-cell iv-cell-${i}`}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => openAt(i)}
              aria-label={`Open image ${i + 1}`}
            >
              <img
                src={src}
                alt={''}
                loading='lazy'
                decoding='async'
                importance='low'
                onError={(e) => {
                  // don't replace with a placeholder image. hide the broken item
                  e.target.onerror = null
                  try {
                    const btn = e.target.closest('.iv-item')
                    if (btn) btn.style.display = 'none'
                  } catch (err) {
                    e.target.style.display = 'none'
                  }
                }}
              />
            </button>
          )
        })}
      </div>

      {open && (
        <div
          className='iv-modal'
          role='dialog'
          aria-modal='true'
          onClick={close}
        >
          <div
            className='iv-modal-content'
            onClick={(e) => e.stopPropagation()}
          >
            <button className='iv-close' onClick={close} aria-label='Close'>
              ✕
            </button>
            <button className='iv-prev' onClick={prev} aria-label='Previous'>
              &larr;
            </button>
            <img
              className='iv-full'
              src={displayCount ? displayImgs[index % displayCount] : ''}
              alt={`Room photo ${index + 1}`}
              loading='eager'
              decoding='async'
              importance='high'
              onError={(e) => {
                // if the modal image fails to load, close the modal rather than show a placeholder
                e.target.onerror = null
                close()
              }}
            />
            <button className='iv-next' onClick={next} aria-label='Next'>
              &rarr;
            </button>
          </div>
        </div>
      )}
    </div>
  )
})

// Amenity helper: renders an icon (emoji) + label in a few small variants
const AmenityItem = ({ emoji, label, className = '' }) => (
  <span className={`amenity-item ${className}`}>
    <span className='amenity-emoji' aria-hidden>
      {emoji}
    </span>
    <span className='amenity-label'>{label}</span>
  </span>
)

// SingleCalendar: shows a month and allows selecting a single date (ISO yyyy-mm-dd)
// accepts `bookedDates` (Set of ISO strings) to mark and disable unavailable dates
const SingleCalendar = ({ selected, onSelect, bookedDates = new Set(), maintenanceDates = new Set(), todayISO = '' }) => {
  const toDate = (s) => (s ? parseISOToLocalDate(s) : null)
  const toISO = (d) => (d ? formatLocalISO(d) : '')
  const selDate = toDate(selected)

  const [leftMonth, setLeftMonth] = useState(() => {
    const base = selDate || new Date()
    return new Date(base.getFullYear(), base.getMonth(), 1)
  })

  useEffect(() => {
    if (selDate)
      setLeftMonth(new Date(selDate.getFullYear(), selDate.getMonth(), 1))
  }, [selected])

  const nextMonth = () =>
    setLeftMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))
  const prevMonth = () =>
    setLeftMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))

  const daysForMonth = (m) => {
    const year = m.getFullYear()
    const month = m.getMonth()
    const first = new Date(year, month, 1)
    const startWeekday = first.getDay()
    const days = []
    const total = new Date(year, month + 1, 0).getDate()
    for (let i = 0; i < startWeekday; i++) days.push(null)
    for (let d = 1; d <= total; d++) days.push(new Date(year, month, d))
    return days
  }

  const isSelected = (d) =>
    d && selDate && d.toDateString() === selDate.toDateString()

  const fmt = (d) =>
    d ? d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : ''

  return (
    <div className='dual-calendar single-calendar'>
      <div className='dual-calendar-header'>
        <div className='dual-calendar-title'>
          {selected ? fmt(selDate) : 'Select date'}
        </div>
        <div className='dual-calendar-range'></div>
      </div>
      <div className='dual-calendar-nav'>
        <button
          type='button'
          className='cal-nav'
          onClick={prevMonth}
          aria-label='Previous month'
        >
          ‹
        </button>
        <div className='cal-months'>
          <div className='cal-month'>
            <div className='cal-month-title'>
              {leftMonth.toLocaleDateString(undefined, {
                month: 'long',
                year: 'numeric',
              })}
            </div>
            <div className='cal-weekdays'>S M T W T F S</div>
            <div className='cal-grid'>
              {daysForMonth(leftMonth).map((d, i) => {
                const iso = d ? formatLocalISO(d) : ''
                const booked = iso && bookedDates && bookedDates.has(iso)
                const maintenance = iso && maintenanceDates && maintenanceDates.has(iso)
                const blocked = booked || maintenance
                return (
                  <button
                    key={i}
                    className={`cal-day ${!d ? 'empty' : ''} ${isSelected(d) ? 'selected' : ''} ${booked ? 'booked' : ''} ${maintenance ? 'maintenance' : ''}`}
                    onClick={() => d && !blocked && onSelect(toISO(d))}
                    aria-label={d ? (blocked ? (maintenance ? 'Under Maintenance' : 'Unavailable') : formatDDMMYYYY(d)) : 'empty'}
                    disabled={!!blocked}
                    title={d ? (blocked ? (maintenance ? 'Under Maintenance' : 'Unavailable') : formatDDMMYYYY(d)) : ''}
                  >
                    <span className='cal-day-number'>{d ? d.getDate() : ''}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// RangePickerInline: lightweight range picker (two months) returning [startISO, endISO]
// accepts `bookedDates` (Set of ISO strings) to visually mark and block unavailable dates
const RangePickerInline = ({ value = [], onChange, startOpen = false, bookedDates = new Set(), maintenanceDates = new Set() }) => {
  const [open, setOpen] = useState(!!startOpen)
  const [start, setStart] = useState(value[0] || '')
  const [end, setEnd] = useState(value[1] || '')

  useEffect(() => {
    setStart(value[0] || '')
    setEnd(value[1] || '')
  }, [value])

  const toDate = (s) => (s ? parseISOToLocalDate(s) : null)
  const toISO = (d) => (d ? formatLocalISO(d) : '')

  const [leftMonth, setLeftMonth] = useState(() => {
    const base = toDate(start) || new Date()
    return new Date(base.getFullYear(), base.getMonth(), 1)
  })

  useEffect(() => {
    if (start) {
      const p = parseISOToLocalDate(start)
      if (p) setLeftMonth(new Date(p.getFullYear(), p.getMonth(), 1))
    }
  }, [start])

  const daysForMonth = (m) => {
    const year = m.getFullYear()
    const month = m.getMonth()
    const first = new Date(year, month, 1)
    const startWeekday = first.getDay()
    const days = []
    const total = new Date(year, month + 1, 0).getDate()
    for (let i = 0; i < startWeekday; i++) days.push(null)
    for (let d = 1; d <= total; d++) days.push(new Date(year, month, d))
    return days
  }

  const handleDateClick = (d) => {
    if (!d) return
    const clickedISO = formatLocalISO(d)
    if (clickedISO && ((bookedDates && bookedDates.has(clickedISO)) || (maintenanceDates && maintenanceDates.has(clickedISO)))) return
    if (!start || (start && end)) {
      setStart(toISO(d))
      setEnd('')
    } else {
      const s = toDate(start)
      if (d < s) {
        setStart(toISO(d))
        setEnd('')
      } else {
        setEnd(toISO(d))
        // commit
        if (onChange) onChange([start, toISO(d)])
      }
    }
  }

  const prevMonth = () =>
    setLeftMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))
  const nextMonth = () =>
    setLeftMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))

  const inRange = (d) => {
    if (!d) return false
    const s = toDate(start)
    const e = toDate(end)
    if (s && e) return d >= s && d <= e
    return false
  }

  const isStart = (d) => {
    const s = toDate(start)
    return d && s && d.toDateString() === s.toDateString()
  }
  const isEnd = (d) => {
    const e = toDate(end)
    return d && e && d.toDateString() === e.toDateString()
  }

  const fmt = (iso) =>
    iso
      ? parseISOToLocalDate(iso).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
      })
      : ''

  return (
    <div className='range-picker-inline'>
      {/* compact input is hidden when startOpen is true */}
      {!startOpen && (
        <div className='range-inputs d-flex gap-3'>
          <button
            type='button'
            className='range-input'
            onClick={() => setOpen((o) => !o)}
          >
            <div className='small text-muted'>CHECK-IN — CHECKOUT</div>
            <div className='range-text'>
              {fmt(start) || 'Start'} — {fmt(end) || 'End'}
            </div>
          </button>
        </div>
      )}
      {open && (
        <div className='range-panel'>
          <div className='panel-head d-flex justify-content-between align-items-center mb-2'>
            <div className='panel-title small text-muted'>Select dates</div>
            <div>
              <button className='cal-nav me-2' onClick={prevMonth}>
                ‹
              </button>
              <button className='cal-nav' onClick={nextMonth}>
                ›
              </button>
            </div>
          </div>
          <div className='cal-months two-months'>
            <div className='cal-month'>
              <div className='cal-month-title'>
                {leftMonth.toLocaleDateString(undefined, {
                  month: 'long',
                  year: 'numeric',
                })}
              </div>
              <div className='cal-weekdays'>S M T W T F S</div>
              <div className='cal-grid'>
                {daysForMonth(leftMonth).map((d, i) => {
                  const iso = d ? formatLocalISO(d) : ''
                  const booked = iso && bookedDates && bookedDates.has(iso)
                  const maintenance = iso && maintenanceDates && maintenanceDates.has(iso)
                  const blocked = booked || maintenance
                  return (
                    <button
                      key={i}
                      className={`cal-day ${!d ? 'empty' : ''} ${inRange(d) ? 'in-range' : ''} ${isStart(d) ? 'selected start' : ''} ${isEnd(d) ? 'selected end' : ''} ${booked ? 'booked' : ''} ${maintenance ? 'maintenance' : ''}`}
                      onClick={() => !blocked && handleDateClick(d)}
                      disabled={!!blocked}
                      title={d ? (blocked ? (maintenance ? 'Under Maintenance' : 'Unavailable') : '') : ''}
                    >
                      <span className='cal-day-number'>
                        {d ? d.getDate() : ''}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
            <div className='cal-month'>
              <div className='cal-month-title'>
                {new Date(
                  leftMonth.getFullYear(),
                  leftMonth.getMonth() + 1,
                  1
                ).toLocaleDateString(undefined, {
                  month: 'long',
                  year: 'numeric',
                })}
              </div>
              <div className='cal-weekdays'>S M T W T F S</div>
              <div className='cal-grid'>
                {daysForMonth(
                  new Date(leftMonth.getFullYear(), leftMonth.getMonth() + 1, 1)
                ).map((d, i) => {
                  const iso = d ? formatLocalISO(d) : ''
                  const booked = iso && bookedDates && bookedDates.has(iso)
                  const maintenance = iso && maintenanceDates && maintenanceDates.has(iso)
                  const blocked = booked || maintenance
                  return (
                    <button
                      key={i}
                      className={`cal-day ${!d ? 'empty' : ''} ${inRange(d) ? 'in-range' : ''} ${isStart(d) ? 'selected start' : ''} ${isEnd(d) ? 'selected end' : ''} ${booked ? 'booked' : ''} ${maintenance ? 'maintenance' : ''}`}
                      onClick={() => !blocked && handleDateClick(d)}
                      disabled={!!blocked}
                      title={d ? (blocked ? (maintenance ? 'Under Maintenance' : 'Unavailable') : '') : ''}
                    >
                      <span className='cal-day-number'>
                        {d ? d.getDate() : ''}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// InteractiveRangeCalendar: calendar that allows date selection and highlights start/end
const InteractiveRangeCalendar = ({
  startISO,
  endISO,
  bookedDates = new Set(),
  maintenanceDates = new Set(),
  onDateSelect,
  todayISO,
}) => {
  const toDate = (s) => (s ? parseISOToLocalDate(s) : null)
  const start = toDate(startISO)
  const end = toDate(endISO)

  // State for tracking date selection mode
  const [selectionMode, setSelectionMode] = useState('start') // 'start' or 'end'

  // allow navigation between months in the calendar
  const [leftMonth, setLeftMonth] = useState(() => {
    const base = start || new Date()
    return new Date(base.getFullYear(), base.getMonth(), 1)
  })

  useEffect(() => {
    if (start) setLeftMonth(new Date(start.getFullYear(), start.getMonth(), 1))
  }, [startISO])

  const prevMonth = () =>
    setLeftMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))
  const nextMonth = () =>
    setLeftMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))

  const daysForMonth = (m) => {
    const year = m.getFullYear()
    const month = m.getMonth()
    const first = new Date(year, month, 1)
    const startWeekday = first.getDay()
    const days = []
    const total = new Date(year, month + 1, 0).getDate()
    for (let i = 0; i < startWeekday; i++) days.push(null)
    for (let d = 1; d <= total; d++) days.push(new Date(year, month, d))
    return days
  }

  const handleDateClick = (date) => {
    if (!date) return

    const clickedISO = formatLocalISO(date)

    // Don't allow selection of past dates, booked dates, or maintenance dates
    if (
      clickedISO < todayISO ||
      bookedDates.has(clickedISO) ||
      maintenanceDates.has(clickedISO)
    )
      return

    if (selectionMode === 'start' || !startISO) {
      // Set as check-in date
      onDateSelect(clickedISO, '')
      setSelectionMode('end')
    } else {
      // Set as check-out date
      if (clickedISO <= startISO) {
        // If clicked date is before or same as start, make it the new start
        onDateSelect(clickedISO, '')
        setSelectionMode('end')
      } else {
        // Valid end date
        onDateSelect(startISO, clickedISO)
        setSelectionMode('start')
      }
    }
  }

  const inRange = (d) => {
    if (!d) return false
    if (start && end) return d >= start && d <= end
    return false
  }

  const isStart = (d) => d && start && d.toDateString() === start.toDateString()
  const isEnd = (d) => d && end && d.toDateString() === end.toDateString()

  const isPastDate = (d) => {
    if (!d) return false
    const iso = formatLocalISO(d)
    return iso < todayISO
  }

  return (
    <div className='interactive-range-calendar'>
      <div className='cal-months one-month'>
        <div className='cal-month' aria-hidden>
          <div className='cal-month-header d-flex align-items-center justify-content-between'>
            <div className='d-flex align-items-center gap'>
              <button
                type='button'
                className='cal-nav small'
                onClick={prevMonth}
                aria-label='Previous month'
              >
                ‹
              </button>
              {/** Use short uppercase month names like JAN, FEB **/}
              {(() => {
                const MONTHS_SHORT = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
                const title = `${MONTHS_SHORT[leftMonth.getMonth()]} ${leftMonth.getFullYear()}`
                return (
                  <div className='cal-month-title'>
                    {title}
                  </div>
                )
              })()}
              <button
                type='button'
                className='cal-nav small'
                onClick={nextMonth}
                aria-label='Next month'
              >
                ›
              </button>
            </div>
                <div className='calendar-legend align-items-center gap-3'>
              <div className='legend-item d-flex align-items-center gap-2'>
                <span className='legend-swatch booked' aria-hidden></span>
                <span className='legend-label small text-muted'>Booked</span>
              </div>
                  <div className='legend-item d-flex align-items-center gap-2'>
                    <span className='legend-swatch maintenance' aria-hidden></span>
                    <span className='legend-label small text-muted'>Maintenance</span>
                  </div>
              <div className='legend-item d-flex align-items-center gap-2'>
                <span className='legend-swatch available' aria-hidden></span>
                <span className='legend-label small text-muted'>Available</span>
              </div>
            </div>
          </div>

          {/* Selection instruction */}
          <div className='text-center mb-2'>
            <small className='text-muted'>
              {selectionMode === 'start'
                ? 'Click to select check-in date'
                : 'Click to select check-out date'}
            </small>
          </div>

          <div className='cal-weekdays'>S M T W T F S</div>
          <div className='cal-grid'>
            {daysForMonth(leftMonth).map((d, i) => {
              const iso = d ? formatLocalISO(d) : ''
              const booked = iso && bookedDates && bookedDates.has(iso)
              const maintenance = iso && maintenanceDates && maintenanceDates.has(iso)
              const past = isPastDate(d)
              const clickable = d && !booked && !past && !maintenance

              return (
                <div
                  key={i}
                  className={`cal-day interactive ${!d ? 'empty' : ''} ${inRange(d) ? 'in-range' : ''} ${isStart(d) ? 'selected start' : ''} ${isEnd(d) ? 'selected end' : ''} ${booked ? 'booked' : ''} ${maintenance ? 'maintenance' : ''} ${past ? 'past' : ''} ${clickable ? 'clickable' : ''}`}
                  onClick={() => handleDateClick(d)}
                  style={{ cursor: clickable ? 'pointer' : 'default' }}
                  title={
                    isStart(d)
                      ? 'Check-in Date'
                      : isEnd(d)
                        ? 'Check-out Date'
                        : maintenance
                          ? 'Scheduled maintenance'
                          : booked
                            ? 'Already Booked'
                            : past
                              ? 'Past Date'
                              : clickable
                                ? 'Click to Select'
                                : ''
                  }
                >
                  <span className='cal-day-number'>{d ? d.getDate() : ''}</span>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

const RoomDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [room, setRoom] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [checkIn, setCheckIn] = useState('')
  const [checkOut, setCheckOut] = useState('')
  const [adults, setAdults] = useState(1)
  const [children, setChildren] = useState(0)
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [addons, setAddons] = useState({ breakfast: false, cycle: false })
  const [bookingError, setBookingError] = useState('')
  const [bookingChecking, setBookingChecking] = useState(false)
  const [bookedDates, setBookedDates] = useState(new Set())
  const [otherRooms, setOtherRooms] = useState([])

  // Individual field errors
  const [fieldErrors, setFieldErrors] = useState({
    name: '',
    phone: '',
    checkIn: '',
    checkOut: '',
    dates: '',
  })

  // today's ISO (local) used for min attribute and validation
  const getTodayISO = () => {
    const d = new Date()
    // ensure local date (no timezone offset issues)
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${yyyy}-${mm}-${dd}`
  }
  const todayISO = getTodayISO()

  // Get minimum checkout date (day after check-in or today, whichever is later)
  const getMinCheckoutDate = () => {
    if (!checkIn) return todayISO

    const checkInDate = new Date(checkIn)
    const nextDay = new Date(checkInDate)
    nextDay.setDate(checkInDate.getDate() + 1)

    const nextDayISO = formatLocalISO(nextDay)
    return nextDayISO > todayISO ? nextDayISO : todayISO
  }



  // check availability: overlapping bookings with status approved/pending
  // NOTE: Some historical bookings may have checkIn/checkOut stored as Timestamps, others as YYYY-MM-DD strings.
  // We query both types and union the results to avoid false availability due to mixed field types.
  const isRoomAvailable = async (roomId, checkInDate, checkOutDate, slots) => {
    try {
      const bookingsRef = collection(db, 'bookings')

      // Build comparable values for both string and timestamp typed fields
      const ciStr = checkInDate
      const coStr = checkOutDate
      const ciDate = parseISOToLocalDate(checkInDate)
      const coDate = parseISOToLocalDate(checkOutDate)

      const results = new Set()

      // Query 1: compare against string fields (YYYY-MM-DD) for APPROVED only
      try {
        const qStr = query(
          bookingsRef,
          where('roomId', '==', roomId),
          where('status', '==', 'approved'),
          where('checkOut', '>', ciStr),
          where('checkIn', '<', coStr)
        )
        const snapStr = await getDocs(qStr)
        snapStr.forEach((d) => results.add(d.id))
      } catch (e) {
        // If index missing or type mismatch, continue with other strategies
        debugWarn('string availability query skipped/failed', e)
      }

      // Query 2: compare against timestamp/date fields (approved only)
      try {
        if (ciDate && coDate) {
          const qTs = query(
            bookingsRef,
            where('roomId', '==', roomId),
            where('status', '==', 'approved'),
            where('checkOut', '>', ciDate),
            where('checkIn', '<', coDate)
          )
          const snapTs = await getDocs(qTs)
          snapTs.forEach((d) => results.add(d.id))
        }
      } catch (e) {
        debugWarn('timestamp availability query skipped/failed', e)
      }

      // If we have N overlapping bookings and there are S slots, it's available if N < S
      let overlappingCount = results.size

      // Fallback: If above queries fail or return suspiciously zero, fetch by room and filter in-memory
      // This avoids composite index requirements at the cost of client-side filtering (acceptable for low volume).
      if (overlappingCount === 0) {
        try {
          const baseSnap = await getDocs(
            query(bookingsRef, where('roomId', '==', roomId))
          )
          baseSnap.forEach((d) => {
            const b = d.data()
            // only consider approved bookings in fallback
            const st = (b.status || '').toLowerCase()
            if (st !== 'approved') return
            // normalize dates from any field variant
            const bci = toISODate(b.checkIn) || toISODate(b.checkInStr) || toISODate(b.checkInTs)
            const bco = toISODate(b.checkOut) || toISODate(b.checkOutStr) || toISODate(b.checkOutTs)
            if (!bci || !bco) return
            // overlap test: (bco > ci) && (bci < co)
            if (bco > ciStr && bci < coStr) results.add(d.id)
          })
          overlappingCount = results.size
        } catch (fallbackErr) {
          debugWarn('fallback availability scan failed', fallbackErr)
        }
      }

      // Availability now ONLY considers approved bookings. Multiple pending bookings may coexist.
      return overlappingCount < (slots || 1)
    } catch (err) {
      debugError('availability check failed', err)
      return false
    }
  }

  // Handle calendar date selection
  const handleCalendarDateSelect = (newCheckIn, newCheckOut) => {
    // Clear any existing field errors related to dates
    setFieldErrors((prev) => ({
      ...prev,
      checkIn: '',
      checkOut: '',
      dates: '',
    }))

    // Disallow selecting a check-in on an already booked or maintenance date
    if (newCheckIn && (bookedDates.has(newCheckIn) || maintenanceDates.has(newCheckIn))) {
      setFieldErrors(prev => ({ ...prev, checkIn: 'Selected check-in date is unavailable. Please pick a different date.' }))
      setCheckIn('')
      setCheckOut('')
      return
    }

    // If both dates provided from the calendar, ensure they don't overlap any booked dates
    if (
      newCheckIn &&
      newCheckOut &&
      (rangeOverlapsBooked(newCheckIn, newCheckOut, bookedDates) || rangeOverlapsBooked(newCheckIn, newCheckOut, maintenanceDates))
    ) {
      setFieldErrors(prev => ({
        ...prev,
        dates: 'Selected dates overlap with existing bookings. Please choose different dates.'
      }))
      setCheckIn(newCheckIn)
      setCheckOut('')
      return
    }

    setCheckIn(newCheckIn)
    setCheckOut(newCheckOut)
  }

  const handleReserve = async () => {
    // Clear all previous errors
    setBookingError('')
    setFieldErrors({
      name: '',
      phone: '',
      checkIn: '',
      checkOut: '',
      dates: '',
    })

    // Validate individual fields
    let hasErrors = false

    if (!name.trim()) {
      setFieldErrors((prev) => ({ ...prev, name: 'Please enter your name.' }))
      hasErrors = true
    } else if (name.trim().length < 3) {
      setFieldErrors((prev) => ({
        ...prev,
        name: 'Name must be at least 3 characters long.',
      }))
      hasErrors = true
    } else if (name.trim().length > 30) {
      setFieldErrors((prev) => ({
        ...prev,
        name: 'Name must not exceed 30 characters.',
      }))
      hasErrors = true
    }

    if (!phone) {
      setFieldErrors((prev) => ({
        ...prev,
        phone: 'Please enter your mobile number.',
      }))
      hasErrors = true
    } else if (phone.length !== 10) {
      setFieldErrors((prev) => ({
        ...prev,
        phone: 'Please enter a valid 10-digit mobile number.',
      }))
      hasErrors = true
    }

    if (!checkIn) {
      setFieldErrors((prev) => ({
        ...prev,
        checkIn: 'Please select check-in date.',
      }))
      hasErrors = true
    }

    if (!checkOut) {
      setFieldErrors((prev) => ({
        ...prev,
        checkOut: 'Please select check-out date.',
      }))
      hasErrors = true
    } else if (checkIn && checkOut && new Date(checkOut) <= new Date(checkIn)) {
      setFieldErrors((prev) => ({
        ...prev,
        checkOut: 'Check-out must be after check-in date.',
      }))
      hasErrors = true
    }

    if (hasErrors) return

    setBookingChecking(true)
    try {
      const roomDoc = await getDoc(doc(db, 'rooms', room.id))
      if (!roomDoc.exists()) {
        setBookingError('Room information not found. Please try again.')
        setBookingChecking(false)
        return
      }
      const roomData = roomDoc.data()
      const slots = roomData.slots || 1

      // Prevent booking if the selected range overlaps scheduled maintenance
      if (maintenanceRange && rangeOverlapsBooked(checkIn, checkOut, maintenanceDates)) {
        setFieldErrors((prev) => ({
          ...prev,
          dates: 'Selected dates overlap with scheduled maintenance. Please choose different dates.',
        }))
        setBookingChecking(false)
        return
      }

      const available = await isRoomAvailable(room.id, checkIn, checkOut, slots)
      if (!available) {
        setFieldErrors((prev) => ({
          ...prev,
          dates:
            'Sorry, this room is fully booked for the selected dates. Please choose different dates.',
        }))
        setBookingChecking(false)
        return
      }

      const totalGuests = (Number(adults) || 0) + (Number(children) || 0)
      const bookingData = {
        name,
        phone,
        roomId: room.id,
        roomName: room.name,
        // Store canonical string dates AND Timestamp-backed dates for robust querying
        checkIn: checkIn,
        checkOut: checkOut,
        checkInStr: checkIn,
        checkOutStr: checkOut,
        checkInTs: parseISOToLocalDate(checkIn) || new Date(checkIn),
        checkOutTs: parseISOToLocalDate(checkOut) || new Date(checkOut),
        // New structure
        adults,
        children,
        status: 'pending',
        createdAt: new Date().toISOString(),
      }

      let bookingId = null
      try {
        const docRef = await addDoc(collection(db, 'bookings'), bookingData)
        bookingId = docRef.id
        // Do not mark dates as booked locally; only show approved bookings on calendar
      } catch (saveErr) {
        debugWarn('Failed to save booking, will still open WhatsApp', saveErr)
      }

      // Format the message for WhatsApp
      const addonsList =
        Object.entries(addons)
          .filter(([_, v]) => v)
          .map(([k]) =>
            k === 'breakfast' ? 'Breakfast' : k === 'cycle' ? 'Cycle Rental' : k
          )
          .join(', ') || 'None'

      // Build booking message parts (reused for WhatsApp and Email)
      const msgParts = [
        'Booking Request for Ampthilldowns',
        '',
        `Name: ${name}`,
        `Phone: ${phone}`,
        `Room: ${room.name}`,
        `Check-in: ${checkIn}`,
        `Check-out: ${checkOut}`,
        `Adults: ${adults}`,
        `Children: ${children}`,
        `Total Guests: ${totalGuests}`,
        `Add-ons: ${addonsList}`,
      ]

      if (bookingId) {
        msgParts.push('')
        msgParts.push(`Booking ID: ${bookingId}`)
      }

      msgParts.push('')
      msgParts.push('Thank you for choosing Ampthilldowns!')

      // Human-readable version for clipboard/display
      const readableMsg = msgParts.join('\n')
      const onMobile = isMobileOrTablet()
      if (onMobile) {
        // Mobile/Tablet: open default mail client via mailto
        const recipient = 'reservations@ampthilldowns.com'
        const subject = 'Booking Request for Ampthilldowns'
        const mailto = `mailto:${encodeURIComponent(recipient)}?subject=${encodeURIComponent(
          subject
        )}&body=${encodeURIComponent(readableMsg)}`
        try {
          window.location.href = mailto
        } catch {
          // Fallback: open in new tab
          try {
            const a = document.createElement('a')
            a.href = mailto
            a.target = '_blank'
            a.rel = 'noopener noreferrer'
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
          } catch {}
        }
      } else {
        // Desktop/Laptop: prefer Gmail compose in Chrome, fall back to mailto
        const recipient = 'reservations@ampthilldowns.com'
        const subject = 'Booking Request for Ampthilldowns'
        const mailto = `mailto:${encodeURIComponent(recipient)}?subject=${encodeURIComponent(
          subject
        )}&body=${encodeURIComponent(readableMsg)}`

        const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(
          recipient
        )}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(
          readableMsg
        )}`
        // If not signed in, send user through Google's account chooser preserving the compose URL
        const accountChooserUrl = `https://accounts.google.com/AccountChooser?service=mail&continue=${encodeURIComponent(
          gmailUrl
        )}`

        // Helper available to try/catch and fallbacks in this scope
        const openInNewTab = (url) => {
          try {
            const win = window.open(url, '_blank', 'noopener,noreferrer')
            if (win && typeof win.focus === 'function') win.focus()
          } catch {
            // Fallback: create anchor targeting _blank
            const a = document.createElement('a')
            a.href = url
            a.rel = 'noopener noreferrer'
            a.target = '_blank'
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
          }
        }

        try {
          // First try account chooser which will redirect back to compose
          openInNewTab(accountChooserUrl)
          // Safety fallback in case popup blocked: try direct compose, then mailto in new tab
          setTimeout(() => {
            try {
              // If the page still visible, likely popup blocked — try direct compose
              if (document.visibilityState === 'visible') {
                openInNewTab(gmailUrl)
                setTimeout(() => {
                  try {
                    if (document.visibilityState === 'visible') {
                      openInNewTab(mailto)
                    }
                  } catch { }
                }, 1200)
              }
            } catch { }
          }, 800)
        } catch (e) {
          debugWarn('Primary email navigation failed, falling back', e)
          try {
            openInNewTab(mailto)
          } catch { }
          // Final safety: copy to clipboard
          if (navigator.clipboard) {
            try {
              await navigator.clipboard.writeText(readableMsg)
              window.alert(
                'Booking details copied! Please paste into your email client to send.'
              )
            } catch { }
          }
        }
      }

      // clear form inputs
      setName('')
      setPhone('')
      setCheckIn('')
      setCheckOut('')
      setAdults(1)
      setChildren(0)
      setAddons({ breakfast: false, cycle: false })
      setBookingError('')
    } catch (err) {
      debugError('Error saving booking', err)
      setBookingError('Failed to book. Please try again.')
    } finally {
      setBookingChecking(false)
    }
  }

  // helper: ensure checkout is after checkin
  const isCheckoutAfterCheckin = (ci, co) => {
    if (!ci || !co) return true
    try {
      const a = parseISOToLocalDate(ci)
      const b = parseISOToLocalDate(co)
      if (!a || !b) return true
      return b > a
    } catch {
      return false
    }
  }

  // helpers to normalize and generate date ranges (YYYY-MM-DD)
  const toISODate = (val) => {
    if (!val) return null
    try {
      // handle Firestore Timestamp objects with toDate
      if (val && typeof val === 'object' && typeof val.toDate === 'function') {
        const d = val.toDate()
        return formatLocalISO(d)
      }
      const d = parseISOToLocalDate(val) || new Date(val)
      if (Number.isNaN(d.getTime())) return null
      return formatLocalISO(d)
    } catch {
      return null
    }
  }

  const getDatesBetween = (startISO, endISO) => {
    const out = []
    if (!startISO || !endISO) return out
    let cur = parseISOToLocalDate(startISO)
    const end = parseISOToLocalDate(endISO)
    while (cur < end) {
      out.push(formatLocalISO(cur))
      cur.setDate(cur.getDate() + 1)
    }
    return out
  }

  // inclusive variant: include the end date as well (useful for maintenance ranges)
  const getDatesBetweenInclusive = (startISO, endISO) => {
    const out = []
    if (!startISO || !endISO) return out
    let cur = parseISOToLocalDate(startISO)
    const end = parseISOToLocalDate(endISO)
    while (cur <= end) {
      out.push(formatLocalISO(cur))
      cur.setDate(cur.getDate() + 1)
    }
    return out
  }

  // maintenance dates derived from room (if present)
  const maintenanceRange = useMemo(() => {
    if (!room) return null
    // Only honor maintenance ranges when the room is explicitly marked MAINTENANCE
    const status = ((room?.status || '') + '').toString().toLowerCase()
    if (status !== 'maintenance') return null
    const s = toISODate(room.maintenanceStartDate)
    const e = toISODate(room.maintenanceEndDate)
    if (s && e) return [s, e]
    return null
  }, [room])

  const maintenanceDates = useMemo(() => {
    if (!maintenanceRange) return new Set()
    const s = getDatesBetweenInclusive(maintenanceRange[0], maintenanceRange[1])
    const out = new Set(s)
    // ensure end date is present (defensive - idempotent)
    if (maintenanceRange[1]) out.add(maintenanceRange[1])
    return out
  }, [maintenanceRange])

  // combined blocked dates include approved bookings + maintenance dates
  const combinedBlockedDates = useMemo(() => {
    const out = new Set(bookedDates)
    for (const d of maintenanceDates) out.add(d)
    return out
  }, [bookedDates, maintenanceDates])

  const rangeOverlapsBooked = (startISO, endISO, bookedSet) => {
    if (!startISO || !endISO) return false
    let cur = parseISOToLocalDate(startISO)
    const end = parseISOToLocalDate(endISO)
    while (cur < end) {
      const iso = formatLocalISO(cur)
      if (bookedSet.has(iso)) return true
      cur.setDate(cur.getDate() + 1)
    }
    return false
  }

  useEffect(() => {
    const fetchRoom = async () => {
      try {
        const ref = doc(db, 'rooms', id)
        const snap = await getDoc(ref)
        if (!snap.exists()) {
          setError('Room not found')
          setLoading(false)
          return
        }
        setRoom({ id: snap.id, ...snap.data() })
      } catch (err) {
        debugError(err)
        setError('Failed to load room')
      } finally {
        setLoading(false)
      }
    }
    fetchRoom()
  }, [id])

  // Fetch up to 4 other rooms (excluding current) once current room is loaded
  useEffect(() => {
    const fetchOthers = async () => {
      if (!room || !room.id) return
      try {
        const snap = await getDocs(collection(db, 'rooms'))
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        // Only include rooms that are AVAILABLE or MAINTENANCE (case-insensitive)
        const allowed = new Set(['available', 'maintenance'])
        const filtered = all
          .filter(r => r.id !== room.id)
          .filter(r => allowed.has(((r.status || '') + '').toString().toLowerCase()))
          .slice(0, 4)
        setOtherRooms(filtered)
      } catch (err) {
        debugWarn('Failed to load other rooms', err)
      }
    }
    fetchOthers()
  }, [room && room.id])

  // fetch bookings for this room and compute booked dates set
  useEffect(() => {
    let mounted = true
    const fetchBooked = async () => {
      if (!room || !room.id) {
        if (mounted) setBookedDates(new Set())
        return
      }
      try {
        const bookingsRef = collection(db, 'bookings')
        let snap
        try {
          const q = query(
            bookingsRef,
            where('roomId', '==', room.id),
            where('status', '==', 'approved')
          )
          snap = await getDocs(q)
        } catch (idxErr) {
          // Fallback if composite index isn't configured: query by roomId only and filter in-memory
          debugWarn('bookedDates query fell back to room-only scan', idxErr)
          snap = await getDocs(query(bookingsRef, where('roomId', '==', room.id)))
        }
        const set = new Set()
        snap.forEach((doc) => {
          const b = doc.data()
          // Try all supported fields for compatibility
          const ci = toISODate(b.checkIn) || toISODate(b.checkInStr) || toISODate(b.checkInTs)
          const co = toISODate(b.checkOut) || toISODate(b.checkOutStr) || toISODate(b.checkOutTs)
          // Only mark dates for approved bookings
          const st = (b.status || '').toLowerCase()
          if (ci && co && st === 'approved') {
            getDatesBetween(ci, co).forEach((d) => set.add(d))
          }
        })
        if (mounted) setBookedDates(set)
      } catch (err) {
        debugWarn('Failed to load booked dates', err)
        if (mounted) setBookedDates(new Set())
      }
    }
    fetchBooked()
    return () => {
      mounted = false
    }
  }, [room && room.id])

  if (loading) return <div className='text-center py-5'>Loading...</div>
  if (error) return <div className='text-center py-5 text-danger'>{error}</div>
  if (!room) return null

  // compute valid image URLs (no hooks so safe here)
  const validImages = Array.isArray(room.images)
    ? room.images.map(getUrl).filter(Boolean)
    : []
  // debug: print valid download URLs and count

  return (
    <div className='room-detail-page'>
      <Container className='py-4'>
        {/* top back link removed per UX change; 'Go back' button added under Reserve */}
        <Row>
          <Col
            md={8}
            className={`gallery-col ${validImages.length > 0 ? '' : 'no-images'
              }`}
          >
            {validImages.length > 0 && (
              <div>
                <ImageViewer images={validImages} />
              </div>
            )}

            {/* Dual two-month calendar (shows selected dates) */}
            {/* calendar will be placed below the details (moved) */}

            <div className='detail-info mt-4'>
              <h1 className='detail-title'>{room.name}</h1>
              {maintenanceRange && ((room?.status || '').toString().toLowerCase() === 'maintenance') && (
                <div className='maintenance-banner mt-2'>
                  <strong>This room is under maintenance</strong>
                  <div className='maintenance-dates'>
                      {formatDDMMYYYY(maintenanceRange[0])} &nbsp; to &nbsp; {formatDDMMYYYY(maintenanceRange[1])}
                    </div>
                </div>
              )}
              <div className='detail-description mt-3'>{room.description}</div>

              {/* Room Details: Basic info */}
              <div className='detail-sub mt-3'>
                {/* compute basic amenities list */}
                {(() => {
                  const cap = room.capacity || 1
                  const bed = (
                    room.bed_type ||
                    room.bedType ||
                    'Bed'
                  ).toString()
                  const baths = room.bathrooms || room.bathroom || 1
                  const amenities = [
                    { emoji: '👥', label: `${cap} guest${cap > 1 ? 's' : ''}` },
                    { emoji: '🛏️', label: bed },
                    {
                      emoji: '🛁',
                      label: `${baths} bathroom${baths > 1 ? 's' : ''}`,
                    },
                  ]

                  return (
                    <>
                      <div className='amenities-inline' aria-hidden>
                        {amenities.map((a, i) => (
                          <React.Fragment key={i}>
                            <span className='amenity-inline-item'>
                              {a.label}
                            </span>
                            {i < amenities.length - 1 && (
                              <span className='dot-sep'> · </span>
                            )}
                          </React.Fragment>
                        ))}
                      </div>

                      <div
                        className='amenities-pills'
                        aria-label='Room amenities'
                      >
                        {amenities.map((a, i) => (
                          <AmenityItem
                            key={i}
                            emoji={a.emoji}
                            label={a.label}
                          />
                        ))}
                      </div>

                      <div className='amenities-large' aria-hidden>
                        {amenities.map((a, i) => (
                          <AmenityItem
                            key={i}
                            emoji={a.emoji}
                            label={a.label}
                            className='large'
                          />
                        ))}
                      </div>
                    </>
                  )
                })()}
              </div>

              {/* Room Amenities: Hotel-style amenities */}
              {room.amenities &&
                Object.values(room.amenities).some(Boolean) && (
                  <div className='room-amenities-section mt-4'>
                    <h3 className='amenities-section-title'>
                      What this place offers
                    </h3>
                    <div className='amenities-cards-grid'>
                      {Object.entries(room.amenities)
                        .filter(([_, enabled]) => enabled)
                        .map(([key, _]) => {
                          const amenityData = {
                            breakfast: { emoji: '🍳', label: 'Breakfast' },
                            fullDayMeal: { emoji: '🍳', label: 'All meals included' },

                            privateBathroom: {
                              emoji: '🚿',
                              label: 'Private bathroom',
                            },
                            balcony: { emoji: '🌅', label: 'Balcony' },
                            airConditioning: {
                              emoji: '❄️',
                              label: 'Air conditioning',
                            },
                            freeWifi: { emoji: '📶', label: 'Free WiFi' },
                            shower: { emoji: '🚿', label: 'Shower' },
                            flatScreenTv: {
                              emoji: '📺',
                              label: 'Flat-screen TV',
                            },
                            frontDesk24: {
                              emoji: '🕐',
                              label: '24-hour front desk',
                            },
                            dailyHousekeeping: {
                              emoji: '🧹',
                              label: 'Daily housekeeping',
                            },
                            lift: { emoji: '🛗', label: 'Lift' },
                            parking: { emoji: '🅿️', label: 'Parking' },
                            roomService: { emoji: '🍽️', label: 'Room service' },
                          }

                          const amenity = amenityData[key]
                          if (!amenity) return null

                          return (
                            <div key={key} className='amenity-card'>
                              <div className='amenity-icon'>
                                {amenity.emoji}
                              </div>
                              <div className='amenity-text'>
                                {amenity.label}
                              </div>
                            </div>
                          )
                        })}
                    </div>
                  </div>
                )}






<section className="rules-compact mobile-hide">
  <h3>House Rules</h3>

  <div className="rules-intro-row">
    <div className="rules-intro">
      <i className="ri-leaf-line"></i>
      You’re staying in a natural environment. Please be mindful and respectful.
    </div>

    <div className="rules-subintro">
      <i className="ri-heart-line"></i>
      Treat the place with respect — this is a quiet natural retreat.
    </div>
  </div>

  <div className="rules-table">
    <div className="divider">Stay Schedule</div>

    <div className="rule rule-yellow">
      <i className="ri-door-open-line"></i>
      Check-in: 12:30 PM – 8:00 PM
    </div>

    <div className="rule rule-yellow">
      <i className="ri-logout-box-line"></i>
      Checkout before 11:00 AM
    </div>

    <div className="divider">Capacity & Conduct</div>

    <div className="rule rule-gray">
      <i className="ri-group-line"></i>
      Maximum 10 guests
    </div>

    <div className="rule rule-gray">
      <i className="ri-time-line"></i>
      Quiet hours: 10:00 PM – 7:00 AM
    </div>

    <div className="rule rule-orange">
      <i className="ri-forbid-line"></i>
      No parties or events
    </div>

    <div className="rule rule-orange">
      <i className="ri-camera-off-line"></i>
      No commercial photography
    </div>

    <div className="rule rule-orange">
      <i className="ri-shield-check-line"></i>
      No illegal activities
    </div>

    <div className="divider">Liability</div>

    <div className="rule rule-red">
      <i className="ri-error-warning-line"></i>
      Forgotten items are not our responsibility
    </div>

    <div className="rule rule-red">
      <i className="ri-bank-card-2-line"></i>
      Damage will be billed at replacement cost
    </div>
  </div>
</section>




                

              {/* Desktop-only similar rooms directly under amenities */}
              {otherRooms && otherRooms.length > 0 && (
                <div className='other-rooms-section mt-4 mobile-hide'>
                  <div className='other-rooms-header'>
                    <h3 className='amenities-section-title'>Other rooms you may like</h3>
                  </div>
                  <div className='other-rooms-cards'>
                    {otherRooms.map(r => {
                      const images = Array.isArray(r.images) ? r.images.map(getUrl).filter(Boolean) : []
                      const img = images[0] || ''
                      const actual = Number(r.actualPrice) || 0
                      const offer = Number(r.offerPrice)
                      const hasOffer = Number.isFinite(offer) && offer > 0 && offer < actual
                      const price = hasOffer ? offer : actual
                      return (
                        <button
                          key={r.id}
                          type='button'
                          className='other-room-card'
                          onClick={() => navigate(`/stays/${r.id}`)}
                          aria-label={`View details for ${r.name}`}
                        >
                          <div className='other-room-thumb'>
                            {img ? (
                              <img src={img} alt={r.name} loading='lazy' decoding='async' />
                            ) : (
                              <div className='other-room-no-image'>No image</div>
                            )}
                          </div>
                          <div className='other-room-meta'>
                            <div className='other-room-name'>{r.name}</div>
                            <div className='other-room-price'>₹{price}</div>
                          </div>
                        </button>
                      )
                    })}
                    <div className='other-rooms-footer'>
                      <button
                        type='button'
                        className='other-rooms-more-link'
                        onClick={() => navigate('/stays')}
                        aria-label='View All Rooms'
                      >
                        View All Rooms <span className='arrow-icon' aria-hidden='true'>→</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}


              {/* Availability Calendar Section (mobile / tablet) */}
            
            </div>
          </Col>
          <Col className='booking-col'>
            {/* Desktop calendar: match booking card width and style */}
            <div className="calendar-desktop booking-card p-4" aria-hidden={false}>
              <h3 className='amenities-section-title'>Availability Calendar</h3>
              <InteractiveRangeCalendar
                startISO={checkIn}
                endISO={checkOut}
                bookedDates={bookedDates}
                maintenanceDates={maintenanceDates}
                onDateSelect={handleCalendarDateSelect}
                todayISO={todayISO}
              />
            </div>
            <div className='booking-card p-4 mt-4'>
              <div className='d-flex justify-content-between align-items-start'>
                <div>
                  <div className='booking-price'>
                    {(() => {
                      const actual = Number(room.actualPrice) || 0
                      const offer = Number(room.offerPrice)
                      const hasOffer =
                        Number.isFinite(offer) && offer > 0 && offer < actual
                      if (hasOffer) {
                        return (
                          <>
                            <div className='price-big'>₹{offer}</div>
                            {/* <div className='price-small text-muted text-decoration-line-through'>
                              ₹{actual}
                            </div> */}
                          </>
                        )
                      }
                      return <div className='price-big'>₹{actual}</div>
                    })()}
                  </div>
                  <div className='price-sub text-muted'>per night for up to {room.capacity} guests; additional guests charged ₹{room.extraPeople} per person.</div>
                </div>
              </div>

              <div className='mt-3 booking-form'>
                <Form onSubmit={(e) => e.preventDefault()}>
                  <Form.Group className='mb-2'>
                    <Form.Label className='small text-muted'>
                      Your Name
                    </Form.Label>
                    <Form.Control
                      type='text'
                      value={name}
                      maxLength={30}
                      onChange={(e) => {
                        const value = e.target.value
                        setName(value)

                        // Real-time validation
                        if (fieldErrors.name) {
                          setFieldErrors((prev) => ({ ...prev, name: '' }))
                        }

                        // Show validation feedback while typing (after user has started typing)
                        if (value.length > 0) {
                          if (value.trim().length < 3) {
                            setFieldErrors((prev) => ({
                              ...prev,
                              name: 'Name must be at least 3 characters long.',
                            }))
                          } else if (value.trim().length > 30) {
                            setFieldErrors((prev) => ({
                              ...prev,
                              name: 'Name must not exceed 30 characters.',
                            }))
                          }
                        }
                      }}
                      disabled={bookingChecking}
                      isInvalid={!!fieldErrors.name}
                    />
                    {fieldErrors.name && (
                      <Form.Control.Feedback type='invalid'>
                        {fieldErrors.name}
                      </Form.Control.Feedback>
                    )}
                    {!fieldErrors.name && name.length > 0 && (
                      <Form.Text className='text-muted small'>
                        {name.trim().length}/30 characters
                      </Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group className='mb-2'>
                    <Form.Label className='small text-muted'>Phone</Form.Label>
                    <Form.Control
                      type='tel'
                      value={phone}
                      onChange={(e) => {
                        setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))
                        if (fieldErrors.phone) {
                          setFieldErrors((prev) => ({ ...prev, phone: '' }))
                        }
                      }}
                      pattern='[0-9]{10}'
                      maxLength={10}
                      disabled={bookingChecking}
                      isInvalid={!!fieldErrors.phone}
                    />
                    {fieldErrors.phone && (
                      <Form.Control.Feedback type='invalid'>
                        {fieldErrors.phone}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>

                  <Row className='g-2'>
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label className='small text-muted'>
                          CHECK-IN
                        </Form.Label>
                        <Form.Control
                          type='date'
                          min={todayISO}
                          value={checkIn}
                          onChange={(e) => {
                            const v = e.target.value
                            // Clear previous errors
                            setFieldErrors((prev) => ({
                              ...prev,
                              checkIn: '',
                              checkOut: '',
                              dates: '',
                            }))

                            // if there is an existing checkout that's before new check-in, clear it
                            if (
                              checkOut &&
                              v &&
                              !isCheckoutAfterCheckin(v, checkOut)
                            ) {
                              setCheckOut('')
                              setFieldErrors((prev) => ({
                                ...prev,
                                dates:
                                  'Check-out was cleared because it was before the new check-in date.',
                              }))
                            }
                            // prevent selecting a past date
                            if (v && v < todayISO) {
                              setFieldErrors((prev) => ({
                                ...prev,
                                checkIn: 'Check-in cannot be in the past.',
                              }))
                              return
                            }
                            // validate that the single selected day is not already booked
                            if (v && (bookedDates.has(v) || maintenanceDates.has(v))) {
                              setFieldErrors((prev) => ({
                                ...prev,
                                checkIn:
                                  'Selected check-in date is unavailable. Please pick a different date.',
                              }))
                              return
                            }
                            // If a checkout already exists, validate the range now (order-agnostic)
                            if (v && checkOut) {
                              if (!isCheckoutAfterCheckin(v, checkOut)) {
                                setCheckOut('')
                                setFieldErrors((prev) => ({
                                  ...prev,
                                  dates:
                                    'Check-out was cleared because it was before the new check-in date.',
                                }))
                              } else if (rangeOverlapsBooked(v, checkOut, bookedDates)) {
                                setCheckOut('')
                                setFieldErrors((prev) => ({
                                  ...prev,
                                  dates:
                                    'Selected dates overlap with existing bookings. Please choose different dates.',
                                }))
                              }
                            }
                            setCheckIn(v)
                          }}
                          disabled={bookingChecking}
                          isInvalid={!!fieldErrors.checkIn}
                        />
                        {fieldErrors.checkIn && (
                          <Form.Control.Feedback type='invalid'>
                            {fieldErrors.checkIn}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label className='small text-muted'>
                          CHECKOUT
                        </Form.Label>
                        <Form.Control
                          type='date'
                          min={getMinCheckoutDate()}
                          value={checkOut}
                          onChange={(e) => {
                            const v = e.target.value
                            // Clear previous errors
                            setFieldErrors((prev) => ({
                              ...prev,
                              checkOut: '',
                              dates: '',
                            }))

                            if (!isCheckoutAfterCheckin(checkIn, v)) {
                              setFieldErrors((prev) => ({
                                ...prev,
                                checkOut:
                                  'Check-out must be after check-in date.',
                              }))
                              // do not set an invalid checkout
                              return
                            }
                            // prevent selecting a past date for checkout
                            if (v && v < todayISO) {
                              setFieldErrors((prev) => ({
                                ...prev,
                                checkOut: 'Check-out cannot be in the past.',
                              }))
                              return
                            }
                            // validate that the chosen range does not overlap existing bookings
                            if (
                              checkIn &&
                              v &&
                              (rangeOverlapsBooked(checkIn, v, bookedDates) || rangeOverlapsBooked(checkIn, v, maintenanceDates))
                            ) {
                              setFieldErrors((prev) => ({
                                ...prev,
                                dates:
                                  'Selected dates overlap with existing bookings. Please choose different dates.',
                              }))
                              return
                            }
                            setCheckOut(v)
                          }}
                          disabled={bookingChecking}
                          isInvalid={!!fieldErrors.checkOut}
                        />
                        {fieldErrors.checkOut && (
                          <Form.Control.Feedback type='invalid'>
                            {fieldErrors.checkOut}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                  {/* Similar rooms are rendered below the main layout, not inside booking card */}

                  {/* Date range error message */}
                  {fieldErrors.dates && (
                    <div className='text-danger small mt-1 mb-2'>
                      {fieldErrors.dates}
                    </div>
                  )}

                  <Form.Group className='mt-3'>
                    <Form.Label className='small text-muted'>GUESTS</Form.Label>
                    <div className='d-flex flex-wrap gap-4'>
                      <div className='guest-stepper'>
                        <span className='small text-muted'>Adults</span>
                        <div className='stepper-input'>
                          <button
                            type='button'
                            className='stepper-btn minus'
                            onClick={() => setAdults((a) => Math.max(1, (Number(a) || 1) - 1))}
                            disabled={bookingChecking || adults <= 1}
                            aria-label='Decrease adults'
                          >
                            −
                          </button>
                          <input
                            type='number'
                            className='stepper-field'
                            value={adults}
                            min={1}
                            step={1}
                            inputMode='numeric'
                            pattern='[0-9]*'
                            onChange={(e) => {
                              const raw = e.target.value
                              const v = parseInt(raw, 10)
                              setAdults(Number.isNaN(v) ? 1 : Math.max(1, v))
                            }}
                            disabled={bookingChecking}
                            aria-label='Number of adults'
                          />
                          <button
                            type='button'
                            className='stepper-btn plus'
                            onClick={() => setAdults((a) => (Number(a) || 1) + 1)}
                            disabled={bookingChecking}
                            aria-label='Increase adults'
                          >
                            +
                          </button>
                        </div>
                      </div>

                      <div className='guest-stepper'>
                        <span className='small text-muted'>Children</span>
                        <div className='stepper-input'>
                          <button
                            type='button'
                            className='stepper-btn minus'
                            onClick={() => setChildren((c) => Math.max(0, (Number(c) || 0) - 1))}
                            disabled={bookingChecking || children <= 0}
                            aria-label='Decrease children'
                          >
                            −
                          </button>
                          <input
                            type='number'
                            className='stepper-field'
                            value={children}
                            min={0}
                            step={1}
                            inputMode='numeric'
                            pattern='[0-9]*'
                            onChange={(e) => {
                              const raw = e.target.value
                              const v = parseInt(raw, 10)
                              setChildren(Number.isNaN(v) ? 0 : Math.max(0, v))
                            }}
                            disabled={bookingChecking}
                            aria-label='Number of children'
                          />
                          <button
                            type='button'
                            className='stepper-btn plus'
                            onClick={() => setChildren((c) => (Number(c) || 0) + 1)}
                            disabled={bookingChecking}
                            aria-label='Increase children'
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  </Form.Group>


                  {/* General booking error (for system errors) */}
                  {bookingError && (
                    <div className='text-danger small mt-2 mb-2'>
                      {bookingError}
                    </div>
                  )}

                  <Button
                    type='button'
                    className='mt-3 w-100 reserve-btn'
                    variant='danger'
                    onClick={() => handleReserve()}
                    disabled={bookingChecking}
                  >
                    {bookingChecking ? 'Checking...' : 'Send by Email'}
                  </Button>
                  <Button
                    type='button'
                    className='mt-2 w-100 go-back-btn'
                    variant='outline-secondary'
                    onClick={() => navigate(-1)}
                  >
                    Go back
                  </Button>
                </Form>
              </div>
              {(() => {
                const v = room && room.tax
                const show =
                  (typeof v === 'string' && v.toLowerCase() === 'yes') ||
                  v === true ||
                  room?.tax_included === true
                return show ? (
                  <div className='mt-3 text-center text-muted small'>
                    Taxes included.
                  </div>
                ) : null
              })()}

            </div>
          </Col>
        </Row>
        {/* Mobile-only House Rules: shown after calendar and booking card */}
        <section className="rules-compact desktop-hide mt-4">
          <h3>House Rules</h3>

          <div className="rules-intro-row">
            <div className="rules-intro">
              <i className="ri-leaf-line"></i>
              You’re staying in a natural environment. Please be mindful and respectful.
            </div>

            <div className="rules-subintro">
              <i className="ri-heart-line"></i>
              Treat the place with respect — this is a quiet natural retreat.
            </div>
          </div>

          <div className="rules-table">
            <div className="divider">Stay Schedule</div>

            <div className="rule rule-yellow">
              <i className="ri-door-open-line"></i>
              Check-in: 12:30 PM – 8:00 PM
            </div>

            <div className="rule rule-yellow">
              <i className="ri-logout-box-line"></i>
              Checkout before 11:00 AM
            </div>

            <div className="divider">Capacity & Conduct</div>

            <div className="rule rule-gray">
              <i className="ri-group-line"></i>
              Maximum 10 guests
            </div>

            <div className="rule rule-gray">
              <i className="ri-time-line"></i>
              Quiet hours: 10:00 PM – 7:00 AM
            </div>

            <div className="rule rule-orange">
              <i className="ri-forbid-line"></i>
              No parties or events
            </div>

            <div className="rule rule-orange">
              <i className="ri-camera-off-line"></i>
              No commercial photography
            </div>

            <div className="rule rule-orange">
              <i className="ri-shield-check-line"></i>
              No illegal activities
            </div>

            <div className="divider">Liability</div>

            <div className="rule rule-red">
              <i className="ri-error-warning-line"></i>
              Forgotten items are not our responsibility
            </div>

            <div className="rule rule-red">
              <i className="ri-bank-card-2-line"></i>
              Damage will be billed at replacement cost
            </div>
          </div>
        </section>
        {/* Similar rooms: separate full-width section for all screens */}
        {otherRooms && otherRooms.length > 0 && (
          <div className='other-rooms-section mt-5 desktop-hide'>
            <div className='other-rooms-header'>
              <h3 className='amenities-section-title'>Other rooms you may like</h3>
            </div>
            <div className='other-rooms-cards'>
              {otherRooms.map(r => {
                const images = Array.isArray(r.images) ? r.images.map(getUrl).filter(Boolean) : []
                const img = images[0] || ''
                const actual = Number(r.actualPrice) || 0
                const offer = Number(r.offerPrice)
                const hasOffer = Number.isFinite(offer) && offer > 0 && offer < actual
                const price = hasOffer ? offer : actual
                return (
                  <button
                    key={r.id}
                    type='button'
                    className='other-room-card'
                    onClick={() => navigate(`/stays/${r.id}`)}
                    aria-label={`View details for ${r.name}`}
                  >
                    <div className='other-room-thumb'>
                      {img ? (
                        <img src={img} alt={r.name} loading='lazy' decoding='async' />
                      ) : (
                        <div className='other-room-no-image'>No image</div>
                      )}
                    </div>
                    <div className='other-room-meta'>
                      <div className='other-room-name'>{r.name}</div>
                      <div className='other-room-price'>₹{price}</div>
                    </div>
                  </button>
                )
              })}
              <div className='other-rooms-footer'>
                <button
                  type='button'
                  className='other-rooms-more-link'
                  onClick={() => navigate('/stays')}
                  aria-label='View All Rooms'
                >
                  View All Rooms <span className='arrow-icon' aria-hidden='true'>→</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </Container>

      {/* Footer */}
      <Footer />

    </div>

  )
}

export default RoomDetail
